import type {
  CompanyProfileResponse,
  CompletionSummary as RorosOverarchingCompletionDTO,
  RorosTargetMarketDTO,
  RorosPersonaDTO,
  RorosIcpDTO,
  RorosIcpPersonaLinkDTO,
  RorosFieldRegistryEntryDTO,
} from '@/types/roros/overarching';
import { fetchJson, mutate, qs } from './base-roros.service';

export interface MatchResult {
  semanticKey: string;
  confidence: number;
  entry: RorosFieldRegistryEntryDTO;
}

// ─── Overarching ────────────────────────────────────────────────────────────

function overarchingCrud<T = { id: string; title: string; description: string | null; sortOrder: number }>(entity: string) {
  return {
    list: (params?: Record<string, string>) =>
      fetchJson<T[]>(`/api/v1/${entity}${qs(params)}`),
    create: (payload: Record<string, unknown>) =>
      mutate<T>(`/api/v1/${entity}`, 'POST', payload),
    get: (id: string) =>
      fetchJson<T>(`/api/v1/${entity}/${id}`),
    update: (id: string, payload: Record<string, unknown>) =>
      mutate<T>(`/api/v1/${entity}/${id}`, 'PUT', payload),
    remove: (id: string) =>
      mutate(`/api/v1/${entity}/${id}`, 'DELETE'),
    reorder: (items: { id: string; sortOrder: number }[]) =>
      mutate(`/api/v1/${entity}/reorder`, 'PUT', { items }),
  };
}

export const RorosOverarchingService = {
  getCompanyProfile: () =>
    fetchJson<CompanyProfileResponse>('/api/v1/company-profile'),
  updateCompanyProfile: (payload: Record<string, unknown>) =>
    mutate<CompanyProfileResponse>('/api/v1/company-profile', 'PUT', payload),
  getAll: () =>
    fetchJson<Record<string, unknown>>('/api/v1/overarching'),
  getCompletion: () =>
    fetchJson<RorosOverarchingCompletionDTO>('/api/v1/overarching/completion') as Promise<RorosOverarchingCompletionDTO>,

  coreValues: overarchingCrud('core-values'),
  companyPrinciples: overarchingCrud('company-principles'),
  usps: overarchingCrud('usps'),
  targetMarkets: overarchingCrud<RorosTargetMarketDTO>('target-markets'),
  personas: overarchingCrud<RorosPersonaDTO>('personas'),
  swtItems: overarchingCrud('swt-items'),
  // visionGoals removed: roros_vision_goals table dropped in migration 381.
  icps: overarchingCrud<RorosIcpDTO>('icps'),

  // ─── ICP-Persona Links ────────────────────────────────────────────────────
  icpPersonaLinks: {
    list: (params?: Record<string, string>) =>
      fetchJson<RorosIcpPersonaLinkDTO[]>(`/api/v1/icp-persona-links${qs(params)}`),
    create: (payload: Record<string, unknown>) =>
      mutate<RorosIcpPersonaLinkDTO>(`/api/v1/icp-persona-links`, 'POST', payload),
    remove: (id: string) =>
      mutate(`/api/v1/icp-persona-links/${id}`, 'DELETE'),
    reorder: (items: { id: string; sortOrder: number }[]) =>
      mutate(`/api/v1/icp-persona-links/reorder`, 'PUT', { items }),
  },

  // ─── Field Registry ────────────────────────────────────────────────────────
  fieldRegistry: {
    list: () => fetchJson<RorosFieldRegistryEntryDTO[]>('/api/v1/field-registry'),
    match: (text: string, locale: 'en' | 'de' = 'en') =>
      fetchJson<MatchResult | null>(`/api/v1/field-registry/match?text=${encodeURIComponent(text)}&locale=${locale}`),
  },

  // ─── AI Research (B28) ──────────────────────────────────────────────────
  research: {
    trigger: (overwriteMode: 'fill_only' | 'overwrite') =>
      mutate<{ jobId: string }>('/api/v1/company-profile/research', 'POST', { overwriteMode }),
    aiProviderStatus: () =>
      fetchJson<{ configured: boolean }>('/api/v1/company-profile/ai-provider-status'),
  },
};

/**
 * Client-side wrapper for the field registry match endpoint.
 * No caching — caller is responsible for debouncing.
 * Forward compatibility hook for voice/chat input in v4.
 */
export async function matchFieldFromText(
  text: string,
  locale: 'en' | 'de' = 'en',
): Promise<MatchResult | null> {
  return RorosOverarchingService.fieldRegistry.match(text, locale);
}
